<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import HelpeeSignUp from '@/components/sections/HelpeeSignUp.vue';
import AnalyticsHelper from '@/helpers/analytics';
import { extractUtmOrCleanUrl } from '@/helpers/url';
import { getTracking } from '@/helpers/tracking';
import { useConvertHexToRgb } from '@/composables/useConvertHexToRgb';
import { useAuthStore } from '@/stores/auth';
import { matchesZodEmail } from '@/utils/emailValidation';

const props = defineProps<{
  isWidget?: boolean;
}>();

const emit = defineEmits<{
  (e: 'success', payload: Record<string, unknown>): void;
}>();

const route = useRoute();
const authStore = useAuthStore();
const { hexColor, rgbColor } = useConvertHexToRgb();

const root = ref<HTMLElement>();
const containerWidth = ref(0);
const isSubmitting = ref(false);
const widget = ref(false);
const initialEmail = ref('');

const getQueryStringInsensitive = (candidates: string[]) => {
  const query = route.query as Record<string, unknown>;

  for (const key of candidates) {
    const direct = query[key];
    if (typeof direct === 'string') {
      return direct;
    }
  }

  const candidateSet = new Set(candidates.map((key) => key.toLowerCase()));
  for (const [key, value] of Object.entries(query)) {
    if (candidateSet.has(key.toLowerCase()) && typeof value === 'string') {
      return value;
    }
  }

  return undefined;
};

const hasHelpersProfilePictures = computed(() =>
  Boolean(
    authStore.helpeeSignUpData?.helpersProfilePicture &&
    authStore.helpeeSignUpData?.helpersProfilePicture?.length > 6
  )
);

const helpersProfilePicture = computed(() => {
  const enoughHelpersPics = hasHelpersProfilePictures.value;

  return [
    {
      picture: enoughHelpersPics
        ? (authStore.helpeeSignUpData?.helpersProfilePicture?.[1] ??
          '/img/team/thibault.png')
        : '/img/team/thibault.png',
      size: 'h-10 w-10',
      margin: '!-mr-1',
      index: 'z-[1]',
    },
    {
      picture: enoughHelpersPics
        ? (authStore.helpeeSignUpData?.helpersProfilePicture?.[2] ??
          '/img/team/hugo.png')
        : '/img/team/hugo.png',
      size: 'h-14 w-14',
      margin: '!-mr-3',
      index: 'z-[2]',
    },
    {
      picture: enoughHelpersPics
        ? (authStore.helpeeSignUpData?.helpersProfilePicture?.[3] ??
          '/img/team/joris.png')
        : '/img/team/joris.png',
      size: 'h-[72px] w-[72px]',
      margin: '!-mr-3',
      index: 'z-[3]',
    },
    {
      picture: enoughHelpersPics
        ? (authStore.helpeeSignUpData?.helpersProfilePicture?.[0] ??
          '/img/team/marine.png')
        : '/img/team/marine.png',
      size: 'h-[88px] w-[88px]',
      margin: '!-mr-3',
      index: 'z-[7]',
    },
    {
      picture: enoughHelpersPics
        ? (authStore.helpeeSignUpData?.helpersProfilePicture?.[4] ??
          '/img/team/nada.png')
        : '/img/team/nada.png',
      size: 'h-[72px] w-[72px]',
      margin: '!-ml-3',
      index: 'z-[6]',
    },
    {
      picture: enoughHelpersPics
        ? (authStore.helpeeSignUpData?.helpersProfilePicture?.[5] ??
          '/img/team/edouard.png')
        : '/img/team/edouard.png',
      size: 'h-14 w-14',
      margin: '!-ml-3',
      index: 'z-[5]',
    },
    {
      picture: enoughHelpersPics
        ? (authStore.helpeeSignUpData?.helpersProfilePicture?.[6] ??
          '/img/team/tom.png')
        : '/img/team/tom.png',
      size: 'h-10 w-10',
      margin: '!-ml-3',
      index: 'z-[4]',
    },
  ];
});

const headerType = computed(() => {
  const value = authStore.helpeeSignUpData?.headerType;
  return value === 'image' || value === 'community' ? value : undefined;
});

const computeContainerWidth = () => {
  containerWidth.value = root.value?.offsetWidth || 0;
};

const setInitialEmail = () => {
  const emailFromQuery = getQueryStringInsensitive(['email', 'Email', 'EMAIL']);
  if (!emailFromQuery) return;

  if (matchesZodEmail(emailFromQuery)) {
    initialEmail.value = emailFromQuery;
  }
};

const handleSubmit = async (payload: {
  email: string;
  consent: boolean;
  turnstileToken?: string;
}) => {
  if (!payload.consent) return;

  isSubmitting.value = true;

  try {
    // Source is the current page URL (where user is when submitting)
    // Comes from 'current_url' param sent by the widget
    const source =
      (route?.query.current_page as string) || (route?.query.currentPage as string) ||
      `${window.location.protocol}//${window.location.host}${window.location.pathname}`;

    // Acquisition source follows priority: source param (from widget) > utm_source > utm_medium > utm_campaign > referrer > 'direct'
    const acquisitionSource =
      (route?.query.source as string) ||
      (route?.query.utm_source as string) ||
      (route?.query.utm_medium as string) ||
      (route?.query.utm_campaign as string) ||
      document.referrer ||
      'direct';

    const data = await authStore.signUpHelpee({
      email: payload.email,
      organizationId: authStore.helpeeSignUpData?.organizationId,
      campaignUuid: route?.params.campaignUuid as string,
      source: extractUtmOrCleanUrl(source),
      acquisitionSource: acquisitionSource
        ? extractUtmOrCleanUrl(acquisitionSource)
        : '',
      // Free-form customer metadata from `window.magma_app[].tracking`, carried in as a base64url
      // query param. Read via getTracking() (memoized) rather than route.query — see helpers/tracking.
      tracking: getTracking(),
    }, payload.turnstileToken);

    emit('success', data);

    AnalyticsHelper.trackEvent('user_ask_match');
  } catch (error) {
    console.error(error);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  widget.value = Boolean(props.isWidget ?? route?.query.widget);

  computeContainerWidth();
  window.addEventListener('resize', computeContainerWidth);

  AnalyticsHelper.trackEvent('user_start_signup');

  setInitialEmail();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', computeContainerWidth);
});

watch(
  () => authStore.helpeeSignUpData?.primaryColor,
  (value) => {
    if (value) {
      hexColor.value = value;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div ref="root">
    <HelpeeSignUp
      :header-type="headerType"
      :header-url="authStore.helpeeSignUpData?.headerUrl"
      :helpers-profile-picture="helpersProfilePicture"
      :helper-highlighted="authStore.helpeeSignUpData?.helperHighlighted"
      :has-helpers-profile-pictures="hasHelpersProfilePictures"
      :widget="widget"
      :container-width="containerWidth"
      :primary-color="authStore.helpeeSignUpData?.primaryColor"
      :rgb-color="rgbColor"
      :terms-url="authStore.helpeeSignUpData?.termsUrl"
      :widget-title="authStore.helpeeSignUpData?.widgetTitle"
      :helper-naming="authStore.helpeeSignUpData?.helperNaming"
      :initial-email="initialEmail"
      :is-submitting="isSubmitting"
      @submit="handleSubmit"
    />
  </div>
</template>
